import React, { useState, useEffect } from 'react';
import { XMarkIcon, PaperAirplaneIcon, CheckIcon } from '@heroicons/react/24/outline';
import gmailSvg from '../../../../../assets/gmail.svg';
import outlookSvg from '../../../../../assets/outlook.svg';

/**
 * Web sidebar version of mobile EmailEditSheet.
 * Edit draft in artifact panel: To, Subject, Body fields, Save (Enter), Send, Close.
 */
const EmailEditSheet = ({
  initialTo,
  initialFrom = '',
  initialSubject,
  initialBody,
  emailSource,
  darkMode,
  onClose,
  onSend,
  onSave,
  sendLoading = false,
  sentAt,
}) => {
  const [toValue, setToValue] = useState(initialTo);
  const [fromValue, setFromValue] = useState(initialFrom);
  const [subjectValue, setSubjectValue] = useState(initialSubject);
  const [bodyValue, setBodyValue] = useState(initialBody);

  useEffect(() => {
    setToValue(initialTo);
    setFromValue(initialFrom);
    setSubjectValue(initialSubject);
    setBodyValue(initialBody);
  }, [initialTo, initialFrom, initialSubject, initialBody]);

  const canSend = Boolean(toValue.trim() && subjectValue.trim() && !sendLoading && !sentAt);
  const isReadOnly = Boolean(sentAt);

  const handleSave = () => {
    if (!onSave || isReadOnly) return;
    onSave({
      to: toValue.trim(),
      from: fromValue.trim(),
      subject: subjectValue.trim(),
      body: bodyValue.trim(),
      source: emailSource,
    });
  };

  const handleKeyDown = (e, isTextarea = false) => {
    if (isTextarea) {
      if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
        e.preventDefault();
        handleSave();
      }
    } else {
      if (e.key === 'Enter') {
        e.preventDefault();
        handleSave();
      }
    }
  };

  const handleSend = async () => {
    if (!canSend) return;
    try {
      await onSend({
        to: toValue.trim(),
        from: '',
        subject: subjectValue.trim(),
        body: bodyValue.trim(),
        source: emailSource,
      });
    } catch {
      return;
    }
    onClose();
  };

  const textColor = darkMode ? '#f4f4f5' : '#111827';
  const labelColor = darkMode ? '#9ca3af' : '#6b7280';
  const dividerColor = darkMode ? '#2c2c2e' : '#e5e7eb';
  const placeholderColor = darkMode ? '#52525b' : '#a1a1aa';
  const sendBtnBg = darkMode ? '#ffffff' : '#000000';
  const sendBtnTextColor = darkMode ? '#000000' : '#ffffff';
  const sendBtnDisabledBg = darkMode ? '#2c2c2e' : '#e5e7eb';

  const logoSrc = emailSource === 'outlook' ? outlookSvg : gmailSvg;

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-[#333333] flex-shrink-0">
        <div className="flex items-center gap-2.5">
          <img src={logoSrc} alt={emailSource} className="w-5 h-5 flex-shrink-0" />
          <span className="text-base font-semibold text-gray-900 dark:text-white">Edit Draft</span>
        </div>
        <div className="flex items-center gap-2">
          {!sentAt && onSave && (
            <button
              onClick={handleSave}
              className="email-squircle-btn flex items-center gap-1.5 px-3 py-2 text-sm font-semibold transition-colors bg-gray-200 dark:bg-[#2c2c2e] text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-[#333]"
              title="Save (Enter)"
            >
              <CheckIcon className="w-4 h-4" />
              Save
            </button>
          )}
          {!sentAt && (
            <button
              onClick={handleSend}
              disabled={!canSend}
              className={`email-squircle-btn flex items-center gap-1.5 px-3 py-2 text-sm font-semibold transition-colors ${
                canSend
                  ? 'bg-black dark:bg-white text-white dark:text-black hover:opacity-90'
                  : 'bg-gray-200 dark:bg-[#2c2c2e] text-gray-500 dark:text-gray-400 cursor-not-allowed'
              }`}
            >
              <PaperAirplaneIcon className={`w-4 h-4 ${sendLoading ? 'animate-pulse' : ''}`} />
              {sendLoading ? 'Sending...' : 'Send'}
            </button>
          )}
          <button
            onClick={onClose}
            className="email-squircle-btn-sm p-2 hover:bg-gray-100 dark:hover:bg-[#222] text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors"
            aria-label="Close"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Form */}
      <div className="flex-1 overflow-auto px-4 py-4 space-y-0">
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1.5" style={{ color: labelColor }}>
              To
            </label>
            <input
              type="email"
              value={toValue}
              onChange={(e) => setToValue(e.target.value)}
              onKeyDown={(e) => handleKeyDown(e, false)}
              placeholder="recipient@email.com"
              disabled={isReadOnly}
              className="w-full text-base py-2 px-0 bg-transparent border-0 border-b focus:outline-none focus:ring-0 focus:border-gray-400 dark:focus:border-gray-500 placeholder-gray-400 dark:placeholder-gray-500"
              style={{
                color: textColor,
                borderColor: dividerColor,
              }}
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1.5" style={{ color: labelColor }}>
              Subject
            </label>
            <input
              type="text"
              value={subjectValue}
              onChange={(e) => setSubjectValue(e.target.value)}
              onKeyDown={(e) => handleKeyDown(e, false)}
              placeholder="Email subject"
              disabled={isReadOnly}
              className="w-full text-base py-2 px-0 bg-transparent border-0 border-b focus:outline-none focus:ring-0 focus:border-gray-400 dark:focus:border-gray-500 placeholder-gray-400 dark:placeholder-gray-500"
              style={{
                color: textColor,
                borderColor: dividerColor,
              }}
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1.5" style={{ color: labelColor }}>
              Body
            </label>
            <textarea
              value={bodyValue}
              onChange={(e) => setBodyValue(e.target.value)}
              onKeyDown={(e) => handleKeyDown(e, true)}
              placeholder="Write your email... (Ctrl+Enter to save)"
              disabled={isReadOnly}
              rows={12}
              className="w-full text-base py-3 px-0 bg-transparent border-0 resize-none focus:outline-none focus:ring-0 min-h-[200px] placeholder-gray-400 dark:placeholder-gray-500"
              style={{
                color: textColor,
                lineHeight: 1.5,
              }}
            />
          </div>
        </div>
      </div>

      {sentAt && (
        <div className="px-4 py-3 border-t border-gray-200 dark:border-[#333333] flex-shrink-0">
          <span className="text-sm text-gray-500 dark:text-gray-400">
            Sent at {new Date(sentAt).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}
          </span>
        </div>
      )}
    </div>
  );
};

export default EmailEditSheet;
